import { useState } from 'react'
import { Input, Select, Tag, Segmented, Upload, message } from 'antd'
import {
  WhatsAppOutlined,
  QrcodeOutlined,
  PictureOutlined,
  LinkOutlined,
  FontSizeOutlined,
  UploadOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { HiOutlineUser, HiOutlineUserGroup } from 'react-icons/hi'
import { usePage, router } from '@inertiajs/react'
import EventsLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import FixedBottomActions from '@/components/layouts/events/FixedBottomActions'
import { Button } from '@/components/ui'
import {
  RecipientSelectionDrawer,
  type RecipientSelectionDrawerProps,
} from '../../components/recipient_selection/RecipientSelectionDrawer'
import {
  type Recipient,
  type RecipientFilters,
  type RecipientFilterOptions,
  defaultRecipientFilters,
} from '../../components/recipient_selection/types'
import {
  type Event,
  type WhatsappTemplate,
  type WhatsappCampaignFormData,
} from '../types'
import { type MergeTagOptions } from '@/lib/mergeTags'
import { useWizardState } from './useWizardState'
import {
  type HeaderImageType,
  type CustomImageMode,
  labelForMergeTagValue,
  headerTextHasPlaceholder,
  MAX_HEADER_IMAGE_SIZE_MB,
  ACCEPTED_IMAGE_TYPES,
} from './helpers'
import WhatsAppPreview from './WhatsAppPreview'
import { api } from '@/lib/apiClient'
import '@/styles/compact-form.css'

const EMPTY_FILTER_OPTIONS: RecipientFilterOptions = { fields: [], sessionChoices: [] }

type PageProps = {
  event: Event
  template: WhatsappTemplate
  initialCampaign: WhatsappCampaignFormData
  availableRecipients?: Recipient[]
  initialRecipientFilters?: RecipientFilters
  filterOptions?: RecipientFilterOptions
  mergeTagOptions: MergeTagOptions
  errors?: Record<string, string | string[]>
  formMode?: 'create' | 'edit'
}

const normalizeError = (err?: string | string[]) =>
  Array.isArray(err) ? err.join(', ') : err

const EditWhatsAppCampaign = () => {
  const {
    event,
    template,
    initialCampaign,
    availableRecipients = [],
    initialRecipientFilters,
    filterOptions = EMPTY_FILTER_OPTIONS,
    mergeTagOptions,
    errors,
    formMode = 'edit',
  } = usePage<PageProps>().props

  const {
    state,
    updateState,
    templateBody,
    allSlots,
    missingSlots,
    selectOptions,
    previewText,
    buildParameterValuesPayload,
  } = useWizardState({
    template,
    initialCampaign,
    initialRecipientFilters,
    mergeTagOptions,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const {
    parameterValues,
    headerImageType,
    headerImageUrl,
    customImageMode,
    isUploading,
    uploadPreviewUrl,
    headerTextValue,
    selectedRecipientIds,
    recipientFilters,
  } = state

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleImageUpload = async (file: File) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      message.error('File must be a PNG, JPEG, or WebP image')
      return false
    }
    if (file.size / 1024 / 1024 >= MAX_HEADER_IMAGE_SIZE_MB) {
      message.error(`File must be smaller than ${MAX_HEADER_IMAGE_SIZE_MB}MB`)
      return false
    }

    updateState({ isUploading: true })
    try {
      const formData = new FormData()
      formData.append('file', file)
      const data = await api.post<{ success: boolean; url: string }>(
        `/events/${event.slug}/messaging/whatsapp_blasts/upload_header_image`,
        formData,
      )
      updateState({
        headerImageUrl: data.url,
        uploadPreviewUrl: URL.createObjectURL(file),
        isUploading: false,
      })
      message.success('Image uploaded successfully')
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Failed to upload image',
      )
      updateState({ isUploading: false })
    }
    return false
  }

  const handleRemoveUploadedImage = () => {
    updateState({ headerImageUrl: '', uploadPreviewUrl: null })
  }

  const handleApplyRecipients: RecipientSelectionDrawerProps['onApply'] = ({
    recipientIds,
    filters,
  }) => {
    updateState({
      selectedRecipientIds: recipientIds,
      recipientFilters: filters,
    })
    setDrawerOpen(false)
  }

  const handleClearRecipients = () => {
    updateState({
      selectedRecipientIds: [],
      recipientFilters: defaultRecipientFilters,
    })
  }

  const getRecipientLabel = () => {
    if (recipientFilters.allAttendees) return 'All Attendees'
    if (selectedRecipientIds.length > 0) {
      return `${selectedRecipientIds.length} recipient${selectedRecipientIds.length > 1 ? 's' : ''} selected`
    }
    return null
  }

  const recipientLabel = getRecipientLabel()

  const handleCancel = () => {
    router.visit(`/events/${event.slug}/messaging/whatsapp_blasts?tab=custom`)
  }

  const handleSubmit = () => {
    if (!state.campaignName.trim()) {
      message.error('Please enter a campaign name')
      return
    }

    if (
      state.selectedRecipientIds.length === 0 &&
      !state.recipientFilters.allAttendees
    ) {
      message.error('Please select at least one recipient')
      return
    }

    // Meta matches body parameters by position and rejects the whole send when one
    // is missing, so an unmapped slot is caught here rather than at delivery.
    if (missingSlots.length > 0) {
      message.error(
        `Map every variable first: ${missingSlots.map((slot) => `{{${slot}}}`).join(', ')}. ` +
          'Pick "Leave blank" for any the event has no value for.',
      )
      return
    }

    setIsSubmitting(true)

    const url =
      formMode === 'edit'
        ? `/events/${event.slug}/messaging/whatsapp_blasts/${initialCampaign.id}`
        : `/events/${event.slug}/messaging/whatsapp_blasts?template_id=${initialCampaign.metaTemplateId}`

    const finalData = {
      whatsapp_campaign: {
        campaign_name: state.campaignName,
        parameter_values: buildParameterValuesPayload(),
        recipient_ids: state.selectedRecipientIds,
        recipient_filters: state.recipientFilters,
        status: initialCampaign.status || 'draft',
      },
    }

    const method = formMode === 'edit' ? 'patch' : 'post'

    router[method](url, finalData, {
      preserveScroll: true,
      onSuccess: () => {
        message.success(
          formMode === 'edit' ? 'Campaign updated' : 'Campaign created',
        )
        router.visit(`/events/${event.slug}/messaging/whatsapp_blasts?tab=custom`, {
          preserveScroll: true,
        })
      },
      onError: (formErrors) => {
        const firstError = formErrors && Object.values(formErrors)[0]
        const normalizedError = Array.isArray(firstError)
          ? firstError[0]
          : firstError
        message.error(normalizedError || 'Failed to save campaign.')
        setIsSubmitting(false)
      },
      onFinish: () => setIsSubmitting(false),
    })
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <EventsLayout>
      <PageHeader
        title="Edit WhatsApp Campaign"
        breadcrumb={[
          {
            title: 'Whatsapp Blast',
            href: `/events/${event.slug}/messaging/whatsapp_blasts`,
          },
          { title: 'Edit' },
        ]}
        showBackButton
        onBack={handleCancel}
      />

      <div className="flex gap-6 px-14 py-6 pb-28">
        {/* Left panel - Form */}
        <div className="w-[440px] flex-shrink-0 space-y-4">
          {/* Campaign name */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Enter campaign name"
              value={state.campaignName}
              onChange={(e) => updateState({ campaignName: e.target.value })}
              status={errors?.campaign_name ? 'error' : undefined}
            />
            {errors?.campaign_name && (
              <div className="mt-1 text-xs text-red-500">
                {normalizeError(errors.campaign_name)}
              </div>
            )}
          </div>

          {/* Header Image Mapping */}
          {template.hasHeaderImage && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <PictureOutlined className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Header Image</span>
              </div>

              <Select<HeaderImageType>
                className="w-full"
                value={headerImageType}
                onChange={(val) => {
                  updateState({ headerImageType: val })
                  if (val === 'attendee.qr_code') updateState({ headerImageUrl: '' })
                }}
                options={[
                  {
                    label: (
                      <span className="flex items-center gap-2">
                        <QrcodeOutlined /> Attendee QR Code
                      </span>
                    ),
                    value: 'attendee.qr_code',
                  },
                  {
                    label: (
                      <span className="flex items-center gap-2">
                        <LinkOutlined /> Custom Image URL
                      </span>
                    ),
                    value: 'custom_url',
                  },
                ]}
              />

              {headerImageType === 'custom_url' && (
                <div className="mt-3 space-y-2">
                  <Segmented
                    block
                    size="small"
                    value={customImageMode}
                    onChange={(val) => {
                      const mode = val as CustomImageMode
                      updateState({ customImageMode: mode })
                      if (mode === 'upload')
                        updateState({
                          headerImageUrl: uploadPreviewUrl ? headerImageUrl : '',
                        })
                    }}
                    options={[
                      { label: 'Upload File', value: 'upload', icon: <UploadOutlined /> },
                      { label: 'Paste URL', value: 'url', icon: <LinkOutlined /> },
                    ]}
                  />

                  {customImageMode === 'url' ? (
                    <Input
                      placeholder="https://example.com/image.png"
                      value={headerImageUrl}
                      onChange={(e) => updateState({ headerImageUrl: e.target.value })}
                      allowClear
                    />
                  ) : uploadPreviewUrl ||
                    (headerImageUrl && !headerImageUrl.startsWith('attendee.')) ? (
                    <div className="relative rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={uploadPreviewUrl || headerImageUrl}
                        alt="Header preview"
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveUploadedImage}
                        className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                      >
                        <DeleteOutlined className="text-red-500 text-xs" />
                      </button>
                    </div>
                  ) : (
                    <Upload.Dragger
                      showUploadList={false}
                      beforeUpload={handleImageUpload}
                      accept=".png,.jpg,.jpeg,.webp"
                      disabled={isUploading}
                      className="!border-gray-200 !bg-gray-50/50"
                    >
                      <div className="flex flex-col items-center py-2">
                        {isUploading ? (
                          <span className="text-xs text-gray-500">Uploading...</span>
                        ) : (
                          <>
                            <UploadOutlined className="text-xl text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">
                              Click or drag image here
                            </span>
                          </>
                        )}
                      </div>
                    </Upload.Dragger>
                  )}
                </div>
              )}

              <div className="mt-2 text-[11px] text-gray-400">
                {headerImageType === 'attendee.qr_code'
                  ? 'A unique QR code image will be generated for each attendee.'
                  : headerImageUrl.trim()
                    ? 'This image will be used for all recipients.'
                    : `Upload or paste a publicly accessible image URL. Max ${MAX_HEADER_IMAGE_SIZE_MB}MB (PNG, JPEG, WebP).`}
              </div>
            </div>
          )}

          {/* Header Text Mapping */}
          {template.hasHeaderText && template.headerText && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <FontSizeOutlined className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Header Text</span>
              </div>

              <div className="mb-3 rounded bg-gray-50 px-3 py-2 font-mono text-[13px] text-gray-700">
                {template.headerText}
              </div>

              {headerTextHasPlaceholder(template) ? (
                <>
                  <div className="mb-1.5 text-xs text-gray-500">
                    Map <span className="font-mono">{'{{1}}'}</span> to a field:
                  </div>
                  <Select
                    className="w-full"
                    placeholder="Select a field..."
                    value={headerTextValue || undefined}
                    onChange={(val: string) => updateState({ headerTextValue: val })}
                    options={selectOptions}
                    showSearch
                    allowClear
                    onClear={() => updateState({ headerTextValue: '' })}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        ?.toLowerCase()
                        .includes(input.toLowerCase()) ?? false
                    }
                  />
                  <div className="mt-2 text-[11px] text-gray-400">
                    The selected value will replace the placeholder in the header.
                  </div>
                </>
              ) : (
                <div className="text-[11px] text-gray-400">
                  This header text has no placeholders and will be sent as-is.
                </div>
              )}
            </div>
          )}

          {/* Body Variable Mapping */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-1 text-sm font-medium text-gray-700">Variable Mapping</div>
            <div className="mb-4 text-xs text-gray-400">
              Map each template placeholder to attendee or event data.
            </div>

            {allSlots.length === 0 ? (
              <div className="rounded border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
                No parameters detected in this template.
              </div>
            ) : (
              <div className="space-y-3">
                {allSlots.map((slot) => (
                  <div key={slot}>
                    <div className="mb-1 flex items-center gap-2">
                      <Tag className="!m-0 !text-[11px] font-mono !bg-gray-50 !border-gray-200 !text-gray-600">
                        {`{{${slot}}}`}
                      </Tag>
                      {parameterValues[String(slot)] && (
                        <span className="text-[11px] text-gray-400">
                          &rarr;{' '}
                          {labelForMergeTagValue(
                            parameterValues[String(slot)],
                            mergeTagOptions,
                          )}
                        </span>
                      )}
                    </div>
                    <Select
                      className="w-full"
                      placeholder="Select a field..."
                      value={parameterValues[String(slot)] || undefined}
                      onChange={(val: string) => {
                        updateState({
                          parameterValues: {
                            ...parameterValues,
                            [String(slot)]: val,
                          },
                        })
                      }}
                      onClear={() => {
                        const next = { ...parameterValues }
                        delete next[String(slot)]
                        updateState({ parameterValues: next })
                      }}
                      options={selectOptions}
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase()) ?? false
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recipient Selection */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-700">
              <HiOutlineUserGroup className="text-lg text-gray-400" />
              Recipients
            </div>

            {recipientLabel ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-lg bg-green-50 border border-green-200 px-3 py-2.5">
                  <HiOutlineUser className="text-green-600 text-base flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-green-800">
                      {recipientLabel}
                    </div>
                  </div>
                  <Tag closable onClose={handleClearRecipients} className="!m-0">
                    {recipientLabel}
                  </Tag>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => setDrawerOpen(true)}
                  className="w-full"
                  size="small"
                >
                  Change Selection
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-center">
                  <HiOutlineUserGroup className="mx-auto text-2xl text-gray-300 mb-1" />
                  <div className="text-xs text-gray-500">No recipients selected</div>
                </div>
                <Button
                  variant="primary"
                  onClick={() => setDrawerOpen(true)}
                  className="w-full"
                  size="small"
                >
                  Select Recipients
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right panel - Preview */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Selected template info (read-only) */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-2">
              <WhatsAppOutlined className="text-[#25D366]" />
              <span className="text-sm font-medium text-gray-700">Selected Template</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-800 font-medium truncate">{template.name}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                {template.category && (
                  <Tag
                    color={
                      template.category.toLowerCase() === 'utility'
                        ? 'blue'
                        : template.category.toLowerCase() === 'marketing'
                          ? 'orange'
                          : 'green'
                    }
                    className="!m-0 !text-[10px] uppercase"
                  >
                    {template.category}
                  </Tag>
                )}
                {template.language && (
                  <span className="text-xs text-gray-400">{template.language}</span>
                )}
              </div>
            </div>
          </div>

          <WhatsAppPreview
            template={template}
            previewText={previewText}
            headerImageType={headerImageType}
            headerImageUrl={headerImageUrl}
            headerTextValue={headerTextValue}
            mergeTagOptions={mergeTagOptions}
            parameterValues={parameterValues}
            allSlots={allSlots}
          />
        </div>
      </div>

      <RecipientSelectionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onApply={handleApplyRecipients}
        recipients={availableRecipients}
        selectedRecipientIds={selectedRecipientIds}
        initialFilters={recipientFilters}
        filterOptions={filterOptions}
        contactField="phone"
      />

      <FixedBottomActions
        onCancel={handleCancel}
        submitText="Save Changes"
        submitLoading={isSubmitting}
        rightActions={
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
            Save Changes
          </Button>
        }
      />
    </EventsLayout>
  )
}

export default EditWhatsAppCampaign
